import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import {
  RichiestaDTO,
  RichiestaModuloDTOStatoValidazione,
  RichiestaModuloDTOVerificaFirmaAutografaAi,
} from '@framework/api-client';
import { TipoDomandaDTO } from '@framework/api-client/src/api/backoffice/schemas';
import { AiModuleCheckMessage } from '@framework/common-web';
import { ManagedSubmitButton } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { ButtonResponsive, FlexRow } from '@framework/ui';
import type { Maybe, ObjectSchema } from '@framework/validation';
import { Divider, Space } from 'antd';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { SubmissionCancelButton } from '../../navigation/buttons/SubmissionCancelButton';
import { CardFooterButtons } from '../../navigation/CardFooterButtons';
import { ISubmissionNavigator } from '../../navigation/useSubmissionNavigation';
import { DownloadOmissisSubmissionButton } from '../../submission/download-pdf/DownloadOmissisSubmissionButton';
import { DownloadSubmissionModuleButton } from '../../submission/download-pdf/DownloadSubmissionModuleButton';
import { SubmissionLogic } from '../../submission/SubmissionLogic';
import { SubmissionModuleState } from '../fsm/SubmissionModuleState';
import { useDraftSaveModuleForm } from '../hooks/useDraftSaveModuleForm';
import { DraftSubmitFormButton } from './submit/DraftSubmitFormButton';
import { SubmissionTitle } from './title/SubmissionTitle';

const SubmissionContent = styled.div`
  padding: 12px 0;
  padding-top: 24px;
`;

const BeforeSubmissionContent = styled.div``;

export interface IModuleFormWrapperProps {
  children?: React.ReactNode;
  currentTitle?: React.ReactNode;
  submissionModuleId: Maybe<string>;
  submissionId: Maybe<string>;
  submission?: RichiestaDTO;
  inquiry?: TipoDomandaDTO;
  validationSchema: ObjectSchema<any>;
  handleNavigation: ISubmissionNavigator;
  validationState: RichiestaModuloDTOStatoValidazione | null | undefined;
  moduleState: SubmissionModuleState;
  hasSignatures: boolean;

  isModuleSigned?: boolean;

  isReadonly?: boolean;

  /** Funzione chiamata al salvataggio avvenuto con successo in bozza */
  onDraftSuccess?: () => Promise<void>;

  /** Spazio prima del module */
  beforeModule?: React.ReactNode;

  /** Indica che la richiesta può essere completata in automatico senza bisogno del completamento manuale dell'utente */
  canAutoComplete?: boolean;

  /** Funzioni che vengono chiamate prima e dopo il salvataggio del modulo (pulsante "Salva") */
  onBeforeSave?: () => Promise<void>;
  onAfterSave?: (isValid: boolean) => Promise<void>;

  /** Indica che ci troviamo nel backoffice */
  isBackoffice?: boolean;

  /** Risultato della verifica della firma autografa tramite AI. */
  verifyHandwrittenSignatureAi?: RichiestaModuloDTOVerificaFirmaAutografaAi;
  /**
   * Indica che il modulo è firmato digitalmente.
   */
  isDigitallySigned?: boolean | null;
}

/**
 * Componente padre che incornicia il form di un modulo con tutte le sue sezioni.
 * Aggiunge i pulsanti di submit e di salvataggio in bozza.
 */
export function ModuleFormWrapper(props: IModuleFormWrapperProps) {
  const {
    children,
    currentTitle,
    submissionModuleId,
    submissionId,
    submission,
    inquiry,
    handleNavigation,
    validationState,
    isReadonly,
    validationSchema,
    moduleState,
    hasSignatures,
    onDraftSuccess,
    beforeModule,
    canAutoComplete,
    onBeforeSave,
    onAfterSave,
    isModuleSigned,
    isBackoffice,
    isDigitallySigned,
    verifyHandwrittenSignatureAi,
  } = props;

  const hideProgressBar =
    SubmissionLogic.isSubmissionSent(submission) || canAutoComplete;

  const { t } = useTranslation();

  const onDraftValidSubmit = useDraftSaveModuleForm({
    submissionModuleId,
    submissionId,
    validationSchema,
    onSuccess: onDraftSuccess,
    onBeforeSave,
    onAfterSave,
  });

  const canNext = useMemo(
    () => handleNavigation.canNext(submissionModuleId),
    [handleNavigation, submissionModuleId],
  );

  const canPrevious = useMemo(
    () => handleNavigation.canPrevious(submissionModuleId),
    [handleNavigation, submissionModuleId],
  );

  const sentable = useMemo(() => {
    return inquiry && !inquiry.flNonInviabile;
  }, [inquiry]);

  // Mostra i pulsanti di download e salvataggio in bozza solo se la richiesta
  // è inviabile o è stata inviata, o se siamo in Backoffice
  const showDownloadAndSaveButtons = useMemo(() => {
    return (
      sentable || isBackoffice || SubmissionLogic.isSubmissionSent(submission)
    );
  }, [submission, isBackoffice, sentable]);

  return (
    <>
      <SubmissionTitle
        currentTitle={currentTitle}
        moduleState={moduleState}
        validationState={validationState}
        hasSignatures={hasSignatures}
        hideProgressBar={hideProgressBar}
      />
      {!beforeModule ? (
        <Divider style={{ margin: 0 }} />
      ) : (
        <BeforeSubmissionContent>{beforeModule}</BeforeSubmissionContent>
      )}
      {isBackoffice && verifyHandwrittenSignatureAi && (
        <AiModuleCheckMessage
          result={verifyHandwrittenSignatureAi}
          isBackoffice={isBackoffice}
          isDigitallySigned={isDigitallySigned}
        />
      )}
      <SubmissionContent>{children}</SubmissionContent>
      <CardFooterButtons>
        <FlexRow
          right={
            <Space>
              {!isReadonly && <SubmissionCancelButton />}
              {showDownloadAndSaveButtons && (
                <>
                  <DownloadSubmissionModuleButton
                    submissionId={submissionId}
                    submissionModuleId={submissionModuleId}
                    isModuleSigned={isModuleSigned}
                    disabled={
                      SubmissionLogic.isSubmissionNew(submission) ||
                      !validationState?.visitato
                    }
                  />
                  {submissionId && (
                    <DownloadOmissisSubmissionButton
                      submissionId={submissionId}
                      disabled={
                        SubmissionLogic.isSubmissionNew(submission) ||
                        !validationState?.visitato
                      }
                    />
                  )}
                  {!isReadonly && (
                    <DraftSubmitFormButton onValid={onDraftValidSubmit} />
                  )}
                </>
              )}
              {canNext &&
                (isReadonly ? (
                  <ButtonResponsive
                    size="large"
                    type="primary"
                    text={t('form.avanti', 'Avanti')}
                    iconRightComponent={ArrowRightOutlined}
                    iconRightAriaLabel={t('form.avanti', 'Avanti')}
                    onClick={() => {
                      if (!submissionModuleId) return;
                      handleNavigation.navigate(submissionModuleId, 'next');
                    }}
                  />
                ) : (
                  <ManagedSubmitButton
                    size="large"
                    type="primary"
                    text={t('form.avanti', 'Avanti')}
                    iconRightComponent={ArrowRightOutlined}
                    iconRightAriaLabel={t('form.avanti', 'Avanti')}
                    // iconRight={<ArrowRightOutlined />}
                    onValid={() => {
                      if (!submissionModuleId) return;
                      setTimeout(() => {
                        handleNavigation.navigate(submissionModuleId, 'next');
                      }, 10);
                    }}
                  />
                ))}
            </Space>
          }
          left={
            canPrevious ? (
              <ButtonResponsive
                size="large"
                type="default"
                text={t('form.indietro', 'Indietro')}
                iconLeftComponent={ArrowLeftOutlined}
                iconLeftAriaLabel={t('form.indietro', 'Indietro')}
                onClick={() => {
                  if (!submissionModuleId) return;
                  setTimeout(() => {
                    handleNavigation.navigate(submissionModuleId, 'previous');
                  }, 10);
                }}
              />
            ) : undefined
          }
        ></FlexRow>
      </CardFooterButtons>
    </>
  );
}
